import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Building2, ShoppingBag, ChevronRight, ShieldCheck, Sparkles, ArrowRight, Search, UserCheck, MessageCircle, Bot, Zap, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AISearchBar from "@/components/AISearchBar";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";

const WORKER_CATEGORIES = {
  "Daily Utility": [
    { skill: "Maid", icon: "\u{1F3E0}" }, { skill: "Cook", icon: "\u{1F373}" }, { skill: "Driver", icon: "\u{1F697}" },
    { skill: "Babysitter", icon: "\u{1F476}" }, { skill: "Elderly Care", icon: "\u{2764}\u{FE0F}\u{200D}\u{1FA79}" },
  ],
  "Skilled Trades": [
    { skill: "Electrician", icon: "\u{26A1}" }, { skill: "Plumber", icon: "\u{1F527}" }, { skill: "Carpenter", icon: "\u{1FA9A}" },
    { skill: "AC Technician", icon: "\u{2744}\u{FE0F}" }, { skill: "Painter", icon: "\u{1F3A8}" },
  ],
  "Semi-Skilled": [
    { skill: "Cleaner", icon: "\u{1F9F9}" }, { skill: "Gardener", icon: "\u{1F331}" }, { skill: "Car Wash Worker", icon: "\u{1F6BF}" },
  ],
};
const ALL_SKILLS = Object.values(WORKER_CATEGORIES).flat().map(s => s.skill);

const HomePage = () => {
  const navigate = useNavigate();
  const [density, setDensity] = useState({ active_workers: 0, total_properties: 0, recent_items: 0, verified_workers: 0 });
  const [form, setForm] = useState({ name: "", skill: "", category: "Daily Utility", contact: "", area: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        await axios.post(`${API}/seed`);
        const res = await axios.get(`${API}/density`);
        setDensity(res.data);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, []);

  const handleWorkerSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.skill || !form.contact) { toast.error("Fill all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/workers`, form);
      toast.success("Welcome to Junction! Verification pending.");
      const waMsg = encodeURIComponent(`New Worker Registration on Junction!\n\nName: ${form.name}\nSkill: ${form.skill}\nPhone: ${form.contact}\nArea: ${form.area || "Not specified"}`);
      window.open(`https://wa.me/${WHATSAPP}?text=${waMsg}`, "_blank");
      setForm({ name: "", skill: "", category: "Daily Utility", contact: "", area: "" });
    } catch { toast.error("Registration failed."); }
    setSubmitting(false);
  };

  const waRegisterLink = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi, I want to register as a worker on Junction. My name is _____, skill: _____, area: _____.")}`;

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* ─── TOP BANNER ─── */}
      <div className="bg-indigo" data-testid="top-banner">
        <div className="text-center py-2.5 border-b border-white/10">
          <p className="text-sm sm:text-base font-heading font-semibold text-white tracking-tight">
            Junction App is launching soon in <span className="text-marigold">Hyderabad</span> — A direct way for residents, local businesses, and workers to connect
          </p>
          <p className="text-xs text-marigold font-semibold mt-1">Zero agents. Zero middlemen.</p>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white/8 rounded-xl px-4 py-2.5 border border-white/10">
            <span className="inline-block text-[10px] font-bold text-terracotta bg-terracotta/20 px-2 py-0.5 rounded-full mb-1">FOR RESIDENTS</span>
            <p className="text-white/90 text-xs leading-relaxed">A platform to find everything nearby — Hire local workers, discover rental flats, buy or sell homes, and trade used items — all within your neighborhood — <span className="text-marigold font-semibold">NO AGENTS</span></p>
          </div>
          <div className="bg-white/8 rounded-xl px-4 py-2.5 border border-white/10">
            <span className="inline-block text-[10px] font-bold text-marigold bg-marigold/20 px-2 py-0.5 rounded-full mb-1">FOR WORKERS</span>
            <p className="text-white/90 text-xs leading-relaxed">People near your home are looking for workers like you — get temporary or permanent jobs with no agents. You work, you take the money directly — <span className="text-marigold font-semibold">NO AGENTS</span></p>
          </div>
        </div>
      </div>

      {/* ─── 3 Main Menus ─── */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="top-menus">
          <button onClick={() => navigate("/hire-workers")} className="group flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-marigold/30 bg-marigold/5 hover:border-marigold hover:shadow-lg transition-all text-center" data-testid="menu-workers">
            <div className="w-16 h-16 rounded-2xl bg-marigold/15 flex items-center justify-center group-hover:scale-110 transition-transform"><Users size={28} className="text-marigold" /></div>
            <h3 className="font-heading font-semibold text-indigo text-lg">Hire Workers</h3>
            <p className="text-xs text-gray-400">Find trusted domestic workers, skilled trades & semi-skilled professionals near you</p>
            <span className="text-xs font-medium text-marigold mt-1 group-hover:underline">Browse Workers &rarr;</span>
          </button>

          <button onClick={() => navigate("/real-estate")} className="group flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-sage/30 bg-sage/5 hover:border-sage hover:shadow-lg transition-all text-center" data-testid="menu-real-estate">
            <div className="w-16 h-16 rounded-2xl bg-sage/15 flex items-center justify-center group-hover:scale-110 transition-transform"><Building2 size={28} className="text-sage" /></div>
            <h3 className="font-heading font-semibold text-indigo text-lg">Rent, Buy, Sell Flats & Homes</h3>
            <p className="text-xs text-gray-400">Zero brokerage. NRI services. Find flats, villas, plots in your neighborhood</p>
            <span className="text-xs font-medium text-sage mt-1 group-hover:underline">Explore Properties &rarr;</span>
          </button>

          <button onClick={() => navigate("/marketplace")} className="group flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-terracotta/30 bg-terracotta/5 hover:border-terracotta hover:shadow-lg transition-all text-center" data-testid="menu-marketplace">
            <div className="w-16 h-16 rounded-2xl bg-terracotta/15 flex items-center justify-center group-hover:scale-110 transition-transform"><ShoppingBag size={28} className="text-terracotta" /></div>
            <h3 className="font-heading font-semibold text-indigo text-lg">Sell Your Products</h3>
            <p className="text-xs text-gray-400">Home food, tutoring, beauty services, household goods — advertise free to your neighborhood</p>
            <span className="text-xs font-medium text-terracotta mt-1 group-hover:underline">Start Selling &rarr;</span>
          </button>
        </div>
      </section>

      {/* ─── WORKER REGISTRATION ─── */}
      <section className="bg-indigo relative overflow-hidden" id="register" data-testid="registration-section">
        <div className="overflow-hidden py-2 bg-marigold">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...ALL_SKILLS, ...ALL_SKILLS, ...ALL_SKILLS].map((s, i) => (
              <span key={i} className="mx-4 text-sm font-bold text-indigo tracking-wide">{s}</span>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 md:py-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div>
              <span className="inline-block text-[10px] font-bold text-marigold uppercase tracking-widest mb-2 bg-marigold/10 px-3 py-1 rounded-full">For Workers</span>
              <h2 className="font-heading text-2xl sm:text-3xl tracking-tight leading-[1.15] font-semibold text-white mb-2">Register for jobs nearby your Home</h2>
              <p className="text-base font-heading font-semibold text-marigold mb-2">SAY NO TO BROKERS</p>
              <p className="text-white/60 text-xs leading-relaxed mb-4 max-w-sm">No Subscription, No Commission. Register and get hired locally.</p>
              <div className="flex flex-wrap gap-3 mb-4">
                <a href={waRegisterLink} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-5 py-4 text-sm font-medium" data-testid="wa-register-button">
                    <MessageCircle size={16} className="mr-2" />Register via WhatsApp
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-5 flex-wrap">
                {["Free registration", "No commission ever", "Hired within 1-5 km"].map((t) => (
                  <div key={t} className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-green-400" /><span className="text-white/60 text-[11px]">{t}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
              <h3 className="font-heading text-sm font-medium text-white mb-3">Quick Registration</h3>
              <form onSubmit={handleWorkerSubmit} className="space-y-2.5">
                <Input placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-white/10 border-white/10 text-white placeholder:text-white/40 h-9 text-sm" data-testid="cta-name-input" />
                <Select value={form.skill} onValueChange={(v) => setForm({ ...form, skill: v })}>
                  <SelectTrigger className="rounded-xl bg-white/10 border-white/10 text-white h-9 text-sm" data-testid="cta-skill-select"><SelectValue placeholder="Select your skill *" /></SelectTrigger>
                  <SelectContent>{ALL_SKILLS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
                <Input placeholder="Phone (+91 XXXXX XXXXX) *" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="rounded-xl bg-white/10 border-white/10 text-white placeholder:text-white/40 h-9 text-sm" data-testid="cta-contact-input" />
                <Input placeholder="Area (e.g., Kokapet, Madhapur)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-xl bg-white/10 border-white/10 text-white placeholder:text-white/40 h-9 text-sm" data-testid="cta-area-input" />
                <Button type="submit" disabled={submitting} className="w-full bg-marigold text-indigo hover:bg-marigold/90 rounded-full py-4 font-medium text-sm group" data-testid="cta-submit-button">
                  {submitting ? "Registering..." : "Join Junction"}<ArrowRight size={16} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-12 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-heading text-2xl font-semibold text-indigo mb-6">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { num: "1", title: "Tell AI what you need", desc: "Type naturally — 'I need a cook in Kokapet' or 'Show flats for rent'. AI understands.", color: "bg-marigold text-indigo" },
              { num: "2", title: "AI finds the best match", desc: "Smart matching across workers, properties, and items. Verified and ranked.", color: "bg-indigo text-white" },
              { num: "3", title: "Connect instantly", desc: "Call or WhatsApp directly. No middlemen. Zero commission.", color: "bg-terracotta text-white" },
            ].map((step) => (
              <div key={step.num} className="flex gap-4 items-start" data-testid={`step-${step.num}`}>
                <div className={`w-10 h-10 rounded-full ${step.color} flex items-center justify-center shrink-0 font-bold text-lg`}>{step.num}</div>
                <div><h3 className="font-heading font-medium text-indigo text-sm mb-1">{step.title}</h3><p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust ─── */}
      <section className="py-12 bg-gray-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-heading text-2xl font-semibold text-indigo mb-6">Your satisfaction, guaranteed</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "AI-Verified", desc: "Every listing auto-checked by AI", color: "bg-green-50 text-green-600" },
              { title: "Zero Charges", desc: "No brokerage. No commissions.", color: "bg-marigold/10 text-marigold" },
              { title: "NRI Friendly", desc: "Field agents for property management", color: "bg-indigo/10 text-indigo" },
              { title: "WhatsApp Support", desc: "Reach us anytime on WhatsApp", color: "bg-terracotta/10 text-terracotta" },
            ].map((t) => (
              <div key={t.title} className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className={`w-10 h-10 rounded-xl ${t.color} flex items-center justify-center mx-auto mb-3`}><ShieldCheck size={18} /></div>
                <h3 className="font-heading font-medium text-indigo text-xs mb-1">{t.title}</h3>
                <p className="text-[11px] text-gray-400">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
